import {Text} from '@visx/text';
import {Grid, XYChart, Tooltip} from '@visx/xychart';
import {format} from 'date-fns';
import {useMemo, useCallback} from 'react';

import {LineChartAxis} from './LineChartAxis';
import {LineChartLine} from './LineChartLine';

import type {LineDatum} from '../utils/types';
import type {AxisScaleOutput} from '@visx/axis';
import type {ScaleConfig} from '@visx/scale';

const CHART_HEIGHT = 500;

type Props<T extends LineDatum> = {
  series: T[];
  forcedYDomain?: [number, number];
  renderYAxisTick?: (
    value: T['items'][number]['count'],
    index: number,
    values: {
      value: T['items'][number]['count'];
      index: number;
    }[]
  ) => string | undefined;
  renderTooltipLabel?: (
    params: Parameters<
      React.ComponentProps<typeof Tooltip<T['items'][number]>>['renderTooltip']
    >[0]
  ) => React.ReactNode;
};
export function LineChart<T extends LineDatum>({
  series,
  forcedYDomain,
  renderYAxisTick,
  renderTooltipLabel,
}: Props<T>): React.JSX.Element {
  const getX = useCallback((d: T['items'][number]) => d.idx, []);
  const getY = useCallback((d: T['items'][number]) => d.count, []);

  if (!series[0]?.items.length) {
    throw new Error('No data provided to LineChart');
  }

  // Create an array of tick values based on the number of items in the first series
  const numTicks = Array(series[0].items.length)
    .fill(0)
    .map((_, i) => i);

  const yScale = useMemo(() => {
    // Calculate the maximum y value from all series
    const allYValues = series.flatMap((s) => s.items.map(getY));
    const maxYValue = Math.max(...allYValues);

    // Create a domain that ensures data stays below the highest tick
    // Add some padding (e.g., 10%) above the max value to create the upper bound
    const yDomainMax = Math.ceil(maxYValue * 1.1);

    const _yScale: ScaleConfig<AxisScaleOutput> = {type: 'linear'};
    // Use forcedYDomain if provided, otherwise use calculated domain
    _yScale.domain = forcedYDomain ?? [0, yDomainMax];

    return _yScale;
  }, [forcedYDomain, getY, series]);

  return (
    <XYChart xScale={{type: 'linear'}} yScale={yScale} height={CHART_HEIGHT}>
      <LineChartAxis
        key="x-axis"
        orientation="bottom"
        tickValues={numTicks}
        tickFormat={
          (x) => x
          // series[0].items[x]
          //   ? format(new Date(series[0].items[x].date), 'MMM do yyyy')
          //   : ''
        }
        tickComponent={({formattedValue, ...tickProps}) => {
          const item = series[0].items[parseInt(formattedValue!)];
          if (!item) return null;

          return (
            <g>
              {item.title && <title>{item.title}</title>}
              <Text {...tickProps} pointerEvents="all" cursor="default">
                {format(new Date(item.date), 'MMM do yyyy')}
              </Text>
            </g>
          );
        }}
      />
      <LineChartAxis
        key="y-axis"
        orientation="left"
        tickFormat={renderYAxisTick}
        hideZero
      />

      <Grid
        key="grid"
        columns={false}
        className="*:stroke-slate6"
        lineStyle={{stroke: undefined}}
      />

      {series.map((data) => (
        <LineChartLine
          key={data.id ?? 'static'}
          data={data}
          getX={getX}
          getY={getY}
        />
      ))}

      {!!renderTooltipLabel && (
        <Tooltip<T['items'][number]>
          showVerticalCrosshair
          snapTooltipToDatumX
          debounce={100}
          offsetTop={-40}
          offsetLeft={40}
          applyPositionStyle
          style={{pointerEvents: 'none'}}
          renderTooltip={(params) => (
            <div className="bg-slate2 text-slate12 dark:outline-slate6 w-72 rounded-md px-2 py-4 text-sm shadow-md dark:outline-1 dark:outline-solid">
              {renderTooltipLabel(params)}
            </div>
          )}
        />
      )}
    </XYChart>
  );
}
